/**
 * RecordatoriosFeed — listado de recordatorios.
 *
 * Modos:
 *   - global (sin `expediente`): muestra los míos + los del equipo (si gestor).
 *   - por expediente (`expediente` definido): muestra solo los del expediente.
 *
 * Acciones por fila: marcar hecho, reabrir, editar, eliminar, llamar.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, ArrowsClockwise, Plus, Phone, ArrowRight, Clock, Check, PencilSimple,
  Trash, X, ArrowCounterClockwise, Warning,
} from "@phosphor-icons/react";
import RecordatorioModal from "./RecordatorioModal";

const ESTADOS = [
  { k: "pending", label: "Pendientes" },
  { k: "overdue", label: "Vencidos" },
  { k: "done", label: "Hechos" },
  { k: "all", label: "Todos" },
];

const SCOPES = [
  { k: "team", label: "Equipo" },
  { k: "mine", label: "Solo míos" },
];

const PRIO_STYLES = {
  alta: "bg-rose-100 text-rose-800 border-rose-200",
  media: "bg-amber-50 text-amber-800 border-amber-200",
  baja: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function relativeLabel(iso) {
  if (!iso) return null;
  const now = Date.now();
  const due = new Date(iso).getTime();
  if (isNaN(due)) return null;
  const diff = due - now;
  const abs = Math.abs(diff);
  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  let txt;
  if (d >= 1) txt = `${d}d ${h}h`;
  else if (h >= 1) txt = `${h}h ${m}m`;
  else txt = `${m}m`;
  return { overdue: diff < 0, label: diff < 0 ? `hace ${txt}` : `en ${txt}` };
}

export default function RecordatoriosFeed({ expediente, currentUser }) {
  const isGestor = ["admin", "gestor_senior", "gestor_siniestros"].includes(currentUser?.role);
  const [estado, setEstado] = useState("pending");
  const [scope, setScope] = useState(isGestor ? "team" : "mine");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ estado });
      if (expediente?.id) {
        params.set("expediente_id", expediente.id);
      } else {
        params.set("scope", scope);
      }
      if (search.trim()) params.set("search", search.trim());
      const r = await api.get(`/recordatorios?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los recordatorios");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [estado, scope, search, expediente?.id]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { pending: 0, overdue: 0, done: 0 };
    for (const r of items) {
      if (r.done) c.done += 1;
      else { c.pending += 1; if (r.overdue) c.overdue += 1; }
    }
    return c;
  }, [items]);

  const markDone = async (r) => {
    try {
      await api.post(`/recordatorios/${r.id}/done`);
      toast.success("Marcado como hecho");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar");
    }
  };

  const reopen = async (r) => {
    try {
      await api.post(`/recordatorios/${r.id}/reopen`);
      toast.success("Recordatorio reabierto");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reabrir");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("¿Eliminar este recordatorio?")) return;
    try {
      await api.delete(`/recordatorios/${r.id}`);
      toast.success("Recordatorio eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const startEdit = (r) => {
    setEditing(r);
    setModalOpen(true);
  };

  const startCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  return (
    <div
      className={expediente ? "py-2" : "px-4 md:px-6 max-w-6xl mx-auto py-2"}
      data-testid="recordatorios-feed"
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1 bg-white border border-border rounded-md p-0.5">
          {ESTADOS.map((e) => (
            <button
              key={e.k}
              onClick={() => setEstado(e.k)}
              data-testid={`recordatorios-filter-estado-${e.k}`}
              className={`text-[11.5px] font-semibold px-2.5 py-1 rounded transition-colors ${
                estado === e.k ? "bg-foreground text-white" : "text-muted hover:text-foreground hover:bg-zinc-50"
              }`}
            >
              {e.label}
              {e.k === "overdue" && counts.overdue > 0 && (
                <span className="ml-1 text-[10px] bg-rose-600 text-white rounded-full px-1.5">{counts.overdue}</span>
              )}
            </button>
          ))}
        </div>

        {!expediente && (
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            data-testid="recordatorios-filter-scope"
            className="text-[11.5px] border border-border rounded-md h-7 px-2 bg-white"
          >
            {SCOPES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
          </select>
        )}

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar..."
          data-testid="recordatorios-search"
          className="text-[12px] border border-border rounded-md h-7 px-2 bg-white flex-1 min-w-[160px]"
        />

        <button
          onClick={load}
          title="Refrescar"
          data-testid="recordatorios-refresh"
          className="text-muted hover:text-foreground p-1"
        >
          <ArrowsClockwise className="w-4 h-4" weight="bold" />
        </button>

        <button
          onClick={startCreate}
          data-testid="recordatorios-new"
          className="text-[11.5px] font-semibold px-2.5 py-1 rounded-md bg-foreground text-white hover:bg-foreground/90 inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" weight="bold" /> Nuevo
        </button>
      </div>

      {/* Listado */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading && items.length === 0 && (
          <div className="py-8 text-center text-muted text-sm">Cargando…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="py-10 text-center text-muted text-sm italic">
            No hay recordatorios {estado !== "all" ? `en estado "${ESTADOS.find((x) => x.k === estado).label.toLowerCase()}"` : ""}.
          </div>
        )}
        {items.length > 0 && (
          <ul className="divide-y divide-border" data-testid="recordatorios-list">
            {items.map((r) => (
              <RecordatorioRow
                key={r.id}
                rec={r}
                currentUser={currentUser}
                onDone={markDone}
                onReopen={reopen}
                onEdit={startEdit}
                onDelete={remove}
                hideExpedienteLink={!!expediente}
              />
            ))}
          </ul>
        )}
      </div>

      {modalOpen && (
        <RecordatorioModal
          existing={editing}
          expediente={expediente}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

function RecordatorioRow({ rec, currentUser, onDone, onReopen, onEdit, onDelete, hideExpedienteLink }) {
  const rel = relativeLabel(rec.fecha_hora);
  const isMine = rec.created_by === currentUser?.id;
  const isAdmin = ["admin", "gestor_senior"].includes(currentUser?.role);
  const canDelete = isMine || isAdmin;
  const tel = rec.telefono || rec.contacto?.telefono;

  return (
    <li
      data-testid={`recordatorio-row-${rec.id}`}
      className={`px-4 md:px-5 py-3 flex items-start gap-3 transition-colors ${
        rec.overdue && !rec.done ? "bg-rose-50/60 hover:bg-rose-50" : "hover:bg-zinc-50"
      } ${rec.done ? "opacity-70" : ""}`}
    >
      <div className={`w-9 h-9 rounded-md border flex items-center justify-center flex-shrink-0 ${
        rec.done
          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
          : rec.overdue
          ? "bg-rose-50 border-rose-200 text-rose-700"
          : "bg-amber-50 border-amber-200 text-amber-700"
      }`}>
        {rec.done
          ? <Check className="w-4 h-4" weight="bold" />
          : rec.overdue
          ? <Warning className="w-4 h-4" weight="duotone" />
          : <Bell className="w-4 h-4" weight="duotone" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-semibold text-[13.5px] text-foreground ${rec.done ? "line-through" : ""}`}>
            {rec.titulo}
          </span>
          <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border ${PRIO_STYLES[rec.prioridad] || PRIO_STYLES.media}`}>
            {rec.prioridad}
          </span>
          {rec.recurrencia && rec.recurrencia !== "none" && (
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-indigo-50 text-indigo-800 border-indigo-200 inline-flex items-center gap-1">
              <ArrowsClockwise className="w-2.5 h-2.5" weight="bold" />
              {rec.recurrencia === "daily" ? "diario" : rec.recurrencia === "weekly" ? "semanal" : "mensual"}
            </span>
          )}
          {rec.done && (
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-emerald-50 text-emerald-800 border-emerald-200">
              Hecho
            </span>
          )}
          {rec.contacto?.nombre && (
            <span className="text-[10.5px] text-muted">· {rec.contacto.nombre}</span>
          )}
        </div>

        {rec.descripcion && (
          <div className="text-[12.5px] text-foreground/80 mt-0.5 line-clamp-2 break-words">
            {rec.descripcion}
          </div>
        )}

        <div className="text-[10.5px] text-muted mt-1.5 inline-flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" weight="bold" />
            {fmtDateTime(rec.fecha_hora)}
          </span>
          {rel && !rec.done && (
            <span className={`font-semibold ${rel.overdue ? "text-rose-700" : "text-foreground/70"}`}>
              {rel.overdue ? "Vencido " : ""}{rel.label}
            </span>
          )}
          {rec.created_by_name && (
            <span>· por {rec.created_by_code || rec.created_by_name}</span>
          )}
          {!hideExpedienteLink && rec.expediente_id && rec.expediente_numero_siniestro && (
            <Link
              to={`/review/${rec.expediente_id}`}
              className="text-brand hover:underline inline-flex items-center gap-1"
              data-testid={`recordatorio-exp-link-${rec.id}`}
            >
              · Exp. {rec.expediente_numero_siniestro} <ArrowRight className="w-2.5 h-2.5" weight="bold" />
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        {tel && !rec.done && (
          <a
            href={`tel:${tel.replace(/\s+/g, "")}`}
            data-testid={`recordatorio-call-${rec.id}`}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
            title={`Llamar a ${tel}`}
          >
            <Phone className="w-3 h-3" weight="bold" /> {tel}
          </a>
        )}
        <div className="flex items-center gap-1">
          {!rec.done && (
            <button
              onClick={() => onDone(rec)}
              data-testid={`recordatorio-done-${rec.id}`}
              title="Marcar como hecho"
              className="text-[11px] font-semibold px-2 py-1 rounded-md bg-foreground text-white hover:bg-foreground/90 inline-flex items-center gap-1"
            >
              <Check className="w-3 h-3" weight="bold" /> Hecho
            </button>
          )}
          {rec.done && (
            <button
              onClick={() => onReopen(rec)}
              data-testid={`recordatorio-reopen-${rec.id}`}
              title="Reabrir"
              className="text-muted hover:text-foreground p-1"
            >
              <ArrowCounterClockwise className="w-3.5 h-3.5" weight="bold" />
            </button>
          )}
          <button
            onClick={() => onEdit(rec)}
            data-testid={`recordatorio-edit-${rec.id}`}
            title="Editar"
            className="text-muted hover:text-foreground p-1"
          >
            <PencilSimple className="w-3.5 h-3.5" weight="bold" />
          </button>
          {canDelete && (
            <button
              onClick={() => onDelete(rec)}
              data-testid={`recordatorio-delete-${rec.id}`}
              title="Eliminar"
              className="text-muted hover:text-rose-600 p-1"
            >
              <Trash className="w-3.5 h-3.5" weight="bold" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
